import { useState } from "react";
import { toast } from "sonner";
import { DownloadSimple, ShareNetwork, ArrowsClockwise, Copy, X, Eye } from "@phosphor-icons/react";
import api from "@/lib/api";
import { resolveAiResponse } from "@/hooks/useAiJob";

/**
 * Recruiter-side action row that sits above the resume block:
 *   [ Re-parse ] [ Download ▾ ] [ Share ]
 *
 * All three actions require the caller to already be authenticated + scoped
 * to the candidate. The download modal exposes a 2×2 grid so the recruiter
 * can pick PDF/DOCX × Minimal/Branded at export time.
 */
export default function ResumeActions({ candidate, onReparsed }) {
    const [downloadOpen, setDownloadOpen] = useState(false);
    const [shareOpen, setShareOpen] = useState(false);
    const [reparsing, setReparsing] = useState(false);
    const [share, setShare] = useState(null);   // {token, expires_at}
    const [creatingShare, setCreatingShare] = useState(false);

    const cid = candidate?.id;

    const reparse = async () => {
        setReparsing(true);
        try {
            const raw = await api.post(`/candidates/${cid}/reparse-resume`);
            // V1.9.0 — endpoint returns {job_id}; poll to completion.
            const data = await resolveAiResponse(raw.data);
            const filled = Array.isArray(data?.fields_filled) ? data.fields_filled : [];
            if (filled.length) {
                const pretty = filled
                    .map((f) => f.replace(/_/g, " "))
                    .slice(0, 6)
                    .join(", ");
                toast.success(
                    `Re-parsed · filled ${filled.length} field${filled.length > 1 ? "s" : ""}: ${pretty}`
                );
            } else {
                toast.success(
                    `Resume re-parsed (${data.chars_before} → ${data.chars_after} chars) · no new fields to fill`
                );
            }
            if (onReparsed) onReparsed();
        } catch (e) {
            toast.error(e?.response?.data?.detail || e?.message || "Re-parse failed");
        } finally {
            setReparsing(false);
        }
    };

    const download = async (fmt, template) => {
        try {
            const url = `${api.defaults.baseURL}/candidates/${cid}/resume.${fmt}?template=${template}`;
            const link = document.createElement("a");
            link.href = url;
            link.rel = "noopener";
            // The endpoint sends Content-Disposition=attachment; the browser will
            // handle the download using the filename from the server.
            document.body.appendChild(link);
            link.click();
            link.remove();
            setDownloadOpen(false);
            toast.success(`Downloading ${fmt.toUpperCase()} · ${template}`);
        } catch (e) {
            toast.error("Download failed");
        }
    };

    const createShare = async () => {
        setCreatingShare(true);
        try {
            const r = await api.post(`/candidates/${cid}/share-link`, {});
            setShare(r.data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't create share link");
        } finally {
            setCreatingShare(false);
        }
    };

    const buildShareUrl = (tok) =>
        `${window.location.origin}/share/resume/${tok}`;

    const copyShare = () => {
        if (!share?.token) return;
        const url = buildShareUrl(share.token);
        navigator.clipboard.writeText(url);
        toast.success("Share link copied to clipboard");
    };

    return (
        <div className="mb-2 flex flex-wrap items-center gap-2">
            <button
                data-testid="resume-reparse-btn"
                onClick={reparse}
                disabled={reparsing || !candidate?.resume_text}
                className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-[#525252] border border-[#E5E5E5] px-2.5 py-1.5 hover:border-ink disabled:opacity-40"
            >
                <ArrowsClockwise size={12} weight="bold" className={reparsing ? "animate-spin" : ""} />
                {reparsing ? "Cleaning…" : "Re-parse"}
            </button>
            <button
                data-testid="resume-download-open-btn"
                onClick={() => setDownloadOpen(true)}
                disabled={!candidate?.resume_text}
                className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-white bg-cobalt px-2.5 py-1.5 hover:bg-cobalt/90 disabled:opacity-40"
            >
                <DownloadSimple size={12} weight="bold" />
                Download
            </button>
            <button
                data-testid="resume-share-open-btn"
                onClick={() => { setShare(null); setShareOpen(true); }}
                disabled={!candidate?.resume_text}
                className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-cobalt border border-cobalt px-2.5 py-1.5 hover:bg-cobalt/5 disabled:opacity-40"
            >
                <ShareNetwork size={12} weight="bold" />
                Share
            </button>

            {/* Download modal */}
            {downloadOpen && (
                <div
                    className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-4"
                    onClick={() => setDownloadOpen(false)}
                    data-testid="download-modal"
                >
                    <div
                        className="bg-white w-full max-w-md p-6 shadow-xl"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex items-start justify-between mb-4">
                            <div>
                                <h3 className="text-lg font-black text-ink">Download resume</h3>
                                <p className="text-xs text-[#A3A3A3]">Pick format + template</p>
                            </div>
                            <button
                                onClick={() => setDownloadOpen(false)}
                                className="text-[#A3A3A3] hover:text-ink"
                                aria-label="Close"
                            >
                                <X size={18} weight="bold" />
                            </button>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            {[
                                { fmt: "pdf", template: "minimal", label: "Minimal PDF", sub: "Clean, client-friendly" },
                                { fmt: "pdf", template: "branded", label: "Branded PDF", sub: "NaukriDaddy header + match badge" },
                                { fmt: "docx", template: "minimal", label: "Minimal DOCX", sub: "Editable in Word" },
                                { fmt: "docx", template: "branded", label: "Branded DOCX", sub: "Editable + branded" },
                            ].map((opt) => (
                                <button
                                    key={`${opt.fmt}-${opt.template}`}
                                    data-testid={`download-${opt.fmt}-${opt.template}`}
                                    onClick={() => download(opt.fmt, opt.template)}
                                    className="text-left border border-[#E5E5E5] p-3 hover:border-ink hover:bg-[#FAFAFA] transition-colors"
                                >
                                    <p className="text-sm font-bold text-ink">{opt.label}</p>
                                    <p className="text-[10px] text-[#A3A3A3] mt-0.5">{opt.sub}</p>
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* Share modal */}
            {shareOpen && (
                <div
                    className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-4"
                    onClick={() => setShareOpen(false)}
                    data-testid="share-modal"
                >
                    <div
                        className="bg-white w-full max-w-lg p-6 shadow-xl"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex items-start justify-between mb-4">
                            <div>
                                <h3 className="text-lg font-black text-ink">Share resume</h3>
                                <p className="text-xs text-[#A3A3A3]">
                                    Link expires in 7 days · recipient must verify email via OTP
                                </p>
                            </div>
                            <button
                                onClick={() => setShareOpen(false)}
                                className="text-[#A3A3A3] hover:text-ink"
                                aria-label="Close"
                            >
                                <X size={18} weight="bold" />
                            </button>
                        </div>
                        {!share ? (
                            <div className="space-y-3">
                                <div className="border-l-2 border-cobalt bg-cobalt/5 p-3 text-xs text-[#525252]">
                                    <p className="font-bold text-cobalt mb-1">Secure share (DPDPA-audited)</p>
                                    <p>Recipient will enter their email → receive a 6-digit code → view the resume as a PDF. Every view is logged with their email + timestamp.</p>
                                </div>
                                <button
                                    data-testid="share-create-btn"
                                    onClick={createShare}
                                    disabled={creatingShare}
                                    className="w-full bg-cobalt text-white text-sm font-bold uppercase tracking-wider py-2.5 hover:bg-cobalt/90 disabled:opacity-40"
                                >
                                    {creatingShare ? "Creating…" : "Create share link"}
                                </button>
                            </div>
                        ) : (
                            <div className="space-y-3">
                                <div>
                                    <p className="text-xs font-bold uppercase tracking-wider text-[#A3A3A3] mb-1">
                                        Shareable link
                                    </p>
                                    <div className="flex items-stretch border border-[#E5E5E5]">
                                        <input
                                            data-testid="share-url-input"
                                            readOnly
                                            value={buildShareUrl(share.token)}
                                            className="flex-1 px-3 py-2 text-xs bg-[#F9F9F9] font-mono"
                                        />
                                        <button
                                            data-testid="share-copy-btn"
                                            onClick={copyShare}
                                            className="border-l border-[#E5E5E5] px-3 hover:bg-[#F5F5F5]"
                                        >
                                            <Copy size={14} weight="bold" />
                                        </button>
                                    </div>
                                    <p className="text-[10px] text-[#A3A3A3] mt-1">
                                        Expires {new Date(share.expires_at).toLocaleDateString()}
                                    </p>
                                </div>
                                <div className="text-xs text-[#525252] bg-[#FAFAFA] p-3">
                                    <p className="flex items-center gap-1.5 text-cobalt font-bold mb-1">
                                        <Eye size={12} weight="bold" />
                                        View log
                                    </p>
                                    <p className="text-[#A3A3A3]">
                                        Views appear here once the recipient verifies their email.
                                    </p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
